import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Titlebar } from '../Titlebar'
import { TitlebarErrorBoundary } from '../TitlebarErrorBoundary'
import { TitlebarProps } from '../Titlebar.types'
import { TitlebarContextProvider, useTitlebarContext } from '../TitlebarContext'
import { usePlatform } from '../../../../hooks/usePlatform'
import { windowInitPropsSchema } from '../../../../lib/ipc/schemas'
import { WindowContextProps, WindowInitProps, WindowContextProviderProps } from './WindowContext.types'

const WindowContext = createContext<WindowContextProps | undefined>(undefined)

const DEFAULT_TITLEBAR: TitlebarProps = {
  title: 'HappyTask',
  icon: 'appIcon.png',
  titleCentered: false,
  hideTitle: false,
  leftContent: undefined,
  rightContent: undefined,
}

export const WindowContextProvider = ({ children, titlebar }: WindowContextProviderProps) => {
  const { isElectron, isMac, isWindows } = usePlatform()

  // Safe default: correct platform from startup, conservative maximizable/minimizable
  // until IPC responds with actual values. Prevents non-null assertion.
  const defaultPlatform: WindowInitProps['platform'] = isElectron ? (isMac ? 'darwin' : isWindows ? 'win32' : 'linux') : 'web'
  const [initProps, setInitProps] = useState<WindowInitProps>({
    width: 0,
    height: 0,
    maximizable: false,
    minimizable: false,
    platform: defaultPlatform,
  })

  // Merge default titlebar props with user defined props
  const mergedTitlebar = useMemo(() => ({ ...DEFAULT_TITLEBAR, ...titlebar }), [titlebar])

  useEffect(() => {
    // Load window init props once on mount (Electron only)
    // Window properties (minimizable, maximizable) are determined by the BrowserWindow type
    // (Setup=false, Onboarding=false, Main=true) and don't change during the window's lifetime.
    // Transitions create new windows, which trigger a fresh mount and init-window call.
    if (isElectron && window.api) {
      window.api.invoke('init-window')
        .then((raw) => {
          const result = windowInitPropsSchema.safeParse(raw)
          if (result.success) {
            setInitProps(result.data)
          } else {
            console.error('[WindowContext] Invalid init-window response:', result.error.format())
          }
        })
        .catch(() => {})
    }
  }, [isElectron])

  const frameClass = isElectron ? 'window-frame' : 'web-frame'

  return (
    <WindowContext.Provider value={{ titlebar: mergedTitlebar, window: initProps }}>
      <TitlebarContextProvider>
        <WindowFrame frameClass={frameClass}>{children}</WindowFrame>
      </TitlebarContextProvider>
    </WindowContext.Provider>
  )
}

/** Inner component that can access TitlebarContext (must be inside TitlebarContextProvider) */
const WindowFrame = ({ frameClass, children }: { frameClass: string; children: React.ReactNode }) => {
  const { registerUnionShapeSlot, registerBumpSlot } = useTitlebarContext()

  return (
    <div className={frameClass}>
      {/* 1. Bump slot FIRST in DOM — paints below everything (DOM-order stacking).
          By placing this before the titlebar, tab labels naturally paint on top of
          the bump without any z-index. This eliminates Chromium compositor reorder bugs. */}
      <div ref={registerBumpSlot} className="union-bump-slot" />
      {/* 2. Titlebar — tabs + labels paint ON TOP of bump by DOM order */}
      <TitlebarErrorBoundary>
        <Titlebar />
      </TitlebarErrorBoundary>
      {/* 3. Content — transparent, bump shows through */}
      <WindowContent>{children}</WindowContent>
      {/* 4. Border slot LAST — paints on top of content */}
      <div ref={registerUnionShapeSlot} className="union-border-slot" />
    </div>
  )
}

const WindowContent = ({ children }: { children: React.ReactNode }) => {
  return <div className="window-content">{children}</div>
}

export const useWindowContext = (): WindowContextProps => {
  const context = useContext(WindowContext)
  if (context === undefined) {
    throw new Error('useWindowContext must be used within a WindowContextProvider')
  }
  return context
}
